import { StrainIndexRiskEnum } from './enum';

export const MIN_ACCEPTABLE_RISK_RPN = 1;
export const MIN_MODERATE_RISK_RPN = 9;
export const MIN_HIGH_RISK_RPN = 36;
export const MIN_VERY_HIGH_RISK_RPN = 125;
export const MIN_SERIOUS_RISK_RPN = 180;

export const MAX_ACCEPTABLE_RISK_RPN = 7;
export const MAX_MODERATE_RISK_RPN = 35;
export const MAX_HIGH_RISK_RPN = 124;
export const MAX_VERY_HIGH_RISK_RPN = 179;
export const MAX_SERIOUS_RISK_RPN = 216;

export const DEFAULT_RISK_RPN_COLOR = '#2F54EB';
export const ACCEPTABLE_RISK_RPN_COLOR = '#2CC852';
export const MODERATE_RISK_RPN_COLOR = '#F8D627';
export const HIGH_RISK_RPN_COLOR = '#F78A38';
export const VERY_HIGH_RISK_RPN_COLOR = '#E74150';
export const SERIOUS_RISK_RPN_COLOR = '#9B54E2';

export const REPORT_NAME_KEYS = {
	BERA: 'bera',
	SERA: 'sera',
	JDS_D86: 'jds_d86',
	JDS_D92: 'jds_d92',
	EWA: 'ewa',
	EWA_D86: 'ewa_d86',
	NOT_SPECIFIED: 'not_specified'
} as const;

export const REPORT_ACRONYM_KEYS: Record<string, string> = {
	BERA: 'B.E.R.A',
	SERA: 'S.E.R.A',
	'JDS D86': 'JDS-D86',
	'JDS D92': 'JDS-D92',
	EWA: 'NR17',
	'EWA JDS-D86': 'NR17 + JDS-D86'
} as const;

export const REPORT_ACRONYMS = {
	BERA: 'BERA',
	SERA: 'SERA',
	JDS_D86: 'JDS-D86',
	JDS_D92: 'JDS-D92',
	EWA: 'EWA',
	EWA_D86: 'EWA JDS-D86'
} as const;

export const TOOL_NAME_KEYS = {
	RULA: 'rula',
	REBA: 'reba',
	ANGLE_TIME: 'angle_time',
	LIBERTY_MUTUAL: 'liberty_mutual',
	NIOSH: 'niosh',
	KIM_PP: 'kim_pp',
	STRAIN_INDEX: 'strain_index',
	KIM_MHO: 'kim_mho',
	BACK_COMPRESSIVE_FORCE: 'back_compressive_force_estimation'
} as const;

export const REPORT_TYPES = {
	BERA: 'bera_report',
	SERA: 'sera_report',
	CUSTOM: 'custom_report'
} as const;

export const REPORT_NAMES_MAP: Record<string, string> = {
	[REPORT_NAME_KEYS.BERA]: 'B.E.R.A.',
	[REPORT_NAME_KEYS.SERA]: 'S.E.R.A.',
	[REPORT_NAME_KEYS.JDS_D86]: 'JDS-D86',
	[REPORT_NAME_KEYS.JDS_D92]: 'JDS-D92',
	[REPORT_NAME_KEYS.EWA]: 'NR17',
	[REPORT_NAME_KEYS.EWA_D86]: 'NR17 + JDS-D86',
	[REPORT_NAME_KEYS.NOT_SPECIFIED]: 'No report specified'
};

export const TOOL_LABELS_MAP: Record<string, string> = {
	[TOOL_NAME_KEYS.RULA]: 'RULA',
	[TOOL_NAME_KEYS.REBA]: 'REBA',
	[TOOL_NAME_KEYS.ANGLE_TIME]: 'Angle by time',
	[TOOL_NAME_KEYS.LIBERTY_MUTUAL]: 'Material handling',
	[TOOL_NAME_KEYS.NIOSH]: 'Manual lifting',
	[TOOL_NAME_KEYS.KIM_PP]: 'Push and pull',
	[TOOL_NAME_KEYS.STRAIN_INDEX]: 'Strain index',
	[TOOL_NAME_KEYS.KIM_MHO]: 'Manual Handling',
	[TOOL_NAME_KEYS.BACK_COMPRESSIVE_FORCE]: 'Back compressive force estimation'
};

export const ACTION_PLAN_COLUMNS_MAP: Record<string, string> = {
	status: 'Status',
	score: 'Score',
	responsible: 'Responsible',
	due_date: 'Due date',
	creation_date: 'Creation date',
	end_date: 'Completion date',
	author: 'Author',
	priority: 'Priority',
	investment: 'Investment',
	origin: 'Source',
	tasks: 'Tasks',
	attachment_number: 'Number of attachments',
	evidence_number: 'Number of evidences',
	progress: 'Progress'
};

export const ICON_REPORT_RADIO_STYLE = { fontSize: '30px' };

export const DATA_EXTRACTION_NOTIFICATION_TYPE = {
	DATA_EXTRACTION_COMPLETED: 'data_extraction_completed'
} as const;

export const DATA_EXTRACTION_NOTIFICATION_METHOD = {
	EMAIL: 'email'
} as const;
export const TOAST_DURATION_RESOLUTION_886 = 3000;

export const HIGH_RISK_COLOR = HIGH_RISK_RPN_COLOR;
export const NONE_RISK_COLOR = '#E0E0E0';
export const SERIOUS_RISK_COLOR = SERIOUS_RISK_RPN_COLOR;
export const MODERATE_RISK_COLOR = MODERATE_RISK_RPN_COLOR;
export const VERY_HIGH_RISK_COLOR = VERY_HIGH_RISK_RPN_COLOR;
export const ACCEPTABLE_RISK_COLOR = ACCEPTABLE_RISK_RPN_COLOR;

export const COUPLING_LABEL = {
	GOOD: 'Good',
	FAIR: 'Fair',
	POOR: 'Poor'
};

export const NIOSH_DURATION_METER = {
	LESS_THAN_1H: { label: '1 hour', value: 1 },
	BETWEEN_1_AND_2H: { label: '1 - 2 hours', value: 2 },
	BETWEEN_2_AND_8H: { label: '2 - 8 hours', value: 8 }
};

export const NIOSH_RISKS = {
	VERY_LOW: { title: 'Very low', color: ACCEPTABLE_RISK_COLOR },
	LOW: { title: 'Low', color: MODERATE_RISK_COLOR },
	MODERATE: { title: 'Moderate', color: HIGH_RISK_COLOR },
	HIGH: { title: 'High', color: VERY_HIGH_RISK_COLOR },
	VERY_HIGH: { title: 'Very high', color: SERIOUS_RISK_COLOR }
};

export const KIM_PP_RISKS = {
	LOW: { title: 'Low', color: ACCEPTABLE_RISK_COLOR },
	MODERATE: { title: 'Moderate', color: MODERATE_RISK_COLOR },
	HIGH: { title: 'High', color: HIGH_RISK_COLOR },
	VERY_HIGH: { title: 'Very high', color: VERY_HIGH_RISK_COLOR }
};

export const KIM_MHO_RISKS = {
	LOW: { title: 'Low', color: ACCEPTABLE_RISK_COLOR },
	SLIGHTLY_INCREASED: { title: 'Slightly increased', color: MODERATE_RISK_COLOR },
	SUBSTANTIALLY_INCREASED: { title: 'Substantially increased', color: HIGH_RISK_COLOR },
	HIGH: { title: 'High', color: VERY_HIGH_RISK_COLOR }
};

export const KIM_MHO_DIAGNOSTICS = {
	LOW: {
		PHYSICAL_OVERLOAD: 'Physical overload is unlikely.',
		HEALTH_CONSEQUENCES: 'No health risk is to be expected.',
		MEASURES: 'None.'
	},
	SLIGHTLY_INCREASED: {
		PHYSICAL_OVERLOAD: 'Physical overload is possible for less resilient persons.',
		HEALTH_CONSEQUENCES: 'Fatigue, low-grade adaptation problems which can be compensated for during leisure time.',
		MEASURES: 'For less resilient persons, workplace redesign and other prevention measures may be helpful.'
	},
	SUBSTANTIALLY_INCREASED: {
		PHYSICAL_OVERLOAD: 'Physical overload is possible for normally resilient persons.',
		HEALTH_CONSEQUENCES:
			'Disorders (pain), possibly including dysfunctions, reversible in most cases, without morphological manifestation.',
		MEASURES: 'Workplace redesign and other prevention measures should be considered.'
	},
	HIGH: {
		PHYSICAL_OVERLOAD: 'Physical overload is likely.',
		HEALTH_CONSEQUENCES:
			'More pronounced disorders and/or dysfunctions, structural damage with pathological significance.',
		MEASURES: 'Workplace redesign measures are necessary. Other prevention measures should be considered.'
	}
};

export const KIM_MHO_FORCE_TRANSFERS = {
	OPTIMUM: {
		title: 'Optimum force application',
		description:
			'Working objects are easy to grip (e.g. bar-shaped, gripping grooves) / good ergonomic gripping design (grips, buttons, tools).'
	},
	RESTRICTED: {
		title: 'Restricted force application',
		description: 'Greater holding forces required / no shaped grips.'
	},
	HINDERED: {
		title: 'Force application considerably hindered',
		description:
			'Working objects hardly possible to grip (slippery, soft, sharp edges) / no or only unsuitable grips.'
	}
};

export const KIM_MHO_FORCE_INTENSITIES = {
	VERY_LOW: {
		title: 'Very low',
		description:
			'Up to 15% Fmax. Button actuation, shifting, ordering, material guidance, insertion of small parts.'
	},
	MODERATE: {
		title: 'Moderate',
		description: 'Up to 30% Fmax. Gripping, joining small work pieces by hand or with small tools.'
	},
	HIGH: {
		title: 'High',
		description:
			'Up to 50% Fmax. Turning, winding, packaging, grasping, holding or joining parts, pressing in, cutting, working with small powered hand tools.'
	},
	VERY_HIGH: {
		title: 'Very high',
		description:
			'Up to 80% Fmax. Cutting involving major element of force, working with small staple guns, moving or holding parts or tools.'
	},
	PEAK: {
		title: 'Peak forces',
		description: 'More than 80% Fmax. Tightening, loosening bolts, separating, pressing in.'
	},
	POWERFUL_HITTING: {
		title: 'Powerful hitting',
		description: 'Hitting with the ball of the thumb, palm of the hand or fist.'
	}
};

export const NONE_RISK_RESULT = 0;
export const ACCEPTABLE_RISK_RESULT = 1;
export const MODERATE_RISK_RESULT = 2;
export const HIGH_RISK_RESULT = 3;
export const VERY_HIGH_RISK_RESULT = 4;
export const SERIOUS_RISK_RESULT = 5;

type RisksMapper = Record<string, { title: string; color: string }>;

export const STRAIN_INDEX_RISKS: RisksMapper = {
	[StrainIndexRiskEnum.SAFE]: { title: 'Safe', color: ACCEPTABLE_RISK_COLOR },
	[StrainIndexRiskEnum.HAZARDOUS]: { title: 'Hazardous', color: VERY_HIGH_RISK_COLOR }
};

export const REBA_MIN_ACCEPTABLE_RISK_SCORE = 1;
export const REBA_MIN_MODERATE_RISK_SCORE = 2;
export const REBA_MIN_HIGH_RISK_SCORE = 4;
export const REBA_MIN_VERY_HIGH_RISK_SCORE = 8;
export const REBA_MIN_SERIOUS_RISK_SCORE = 11;

export const REBA_GRADES = [
	{ title: 'Not identified', color: NONE_RISK_COLOR },
	{ title: 'Safe', color: ACCEPTABLE_RISK_COLOR },
	{ title: 'Info', color: MODERATE_RISK_COLOR },
	{ title: 'Attention', color: HIGH_RISK_COLOR },
	{ title: 'Warning', color: VERY_HIGH_RISK_COLOR },
	{ title: 'Danger', color: SERIOUS_RISK_COLOR }
];

export const REBA_BODY_PART_GRADES = [
	{ title: 'Not identified', color: NONE_RISK_COLOR },
	{ title: 'Safe', color: ACCEPTABLE_RISK_COLOR },
	{ title: 'Info', color: MODERATE_RISK_COLOR },
	{ title: 'Warning', color: VERY_HIGH_RISK_COLOR },
	{ title: 'Danger', color: SERIOUS_RISK_COLOR }
];
